import { useKeycloak } from '@react-keycloak/web';
import {
  ForwardedRef,
  Fragment,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { Divider, Stack, useTheme } from '@mui/material';

import { useQuery } from '@tanstack/react-query';
import { useShallow } from 'zustand/react/shallow';

import { LocationResponse, OrganizationResponse } from '@/api/data-contracts';
import useSidemenuStore from '@/app/sidemenu.store';
import {
  SideMenuItem,
  SideMenuSection,
  getAdminSideMenuSections,
  getAdvertisersMenuSections,
  getLocationSideMenuSections,
  getServicesMenuSections,
} from '@/app/ui/SideMenu/config';
import { Container, NavigationContainer } from '@/app/ui/SideMenu/styled';
import { useCompaniesService } from '@/features/company';
import { useLocationsListQuery } from '@/features/locations';
import { useUsersService } from '@/features/users';
import { queryKeys } from '@/react-query/constants';
import { UserTypeContext } from '@/shared/lib/UserTypeContext/UserTypeContext';

import GeneratedAwbs from '../GeneratedAwbs';
import ShoppingCart from '../ShoppingCart';
import AccountMenu from './AccountMenu';
import NotificationsButton from './NotificationsButton';
import SideMenuListItem from './SideMenuListItem';
import SideMenuLogo from './SideMenuLogo';

const SideMenu = forwardRef(function SideMenu(_, ref: ForwardedRef<HTMLDivElement | null>) {
  const { t } = useTranslation();
  const theme = useTheme();
  const { keycloak } = useKeycloak();
  const location = useLocation();
  const userType = useContext(UserTypeContext);
  const { showOrganization } = useCompaniesService();
  const { hasPermission } = useUsersService();

  const { isExpanded: isExpandedFromStore, setIsExpanded } = useSidemenuStore(
    useShallow((state) => ({ isExpanded: state.isExpanded, setIsExpanded: state.setIsExpanded })),
  );

  const [showSubmenu, setShowSubmenu] = useState<boolean>(false);
  const closeActiveHoverRef = useRef<(() => void) | null>(null);
  const handleHoverStart = useCallback((closeSelf: () => void) => {
    closeActiveHoverRef.current?.();
    closeActiveHoverRef.current = closeSelf;
  }, []);
  const [organizationId, setOrganizationId] = useState<string>('');
  const [isGuestSidebar, setIsGuestSidebar] = useState<boolean | null>(null);

  const isExpanded = isGuestSidebar === false ? isExpandedFromStore : false;

  const [hasPermissionToViewServiceSuppliers, setHasPermissionToViewServiceSupplier] =
    useState<boolean>(false);

  const isValidUUID = (uuid: string): boolean => {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return uuidRegex.test(uuid);
  };

  useEffect(() => {
    if (keycloak.authenticated) {
      keycloak.loadUserInfo().then((u: unknown) => {
        const userInfo = u as { organization?: string };
        setOrganizationId(userInfo.organization ?? '');
        setIsGuestSidebar(!userInfo.organization);
      });
    }
  }, [keycloak]);

  useEffect(() => {
    const checkPermission = async () => {
      const permission = await hasPermission('edit:services-admin');
      setHasPermissionToViewServiceSupplier(permission);
    };

    if (userType === 'service') {
      checkPermission();
    }
  }, [hasPermission, userType]);

  const sideMenuLocationsRequest = useMemo(() => ({ pageNumber: 0, numberOfItems: 1000 }), []);
  const { data: locations } = useLocationsListQuery(
    sideMenuLocationsRequest,
    userType === 'location',
  );

  const { data: organization } = useQuery<OrganizationResponse>({
    queryKey: [queryKeys.currentUser, 'organization', organizationId],
    queryFn: ({ signal }) => showOrganization(organizationId, signal),
    enabled: !!organizationId,
  });

  const malls = useMemo<Record<string, LocationResponse[]>>(() => {
    if (!locations?.content) return {};

    return Object.fromEntries(
      Array.from(
        locations.content
          .filter((l) => l.locationType === 'SHOPPING_MALL')
          .reduce((map, location) => {
            const key = location.company?.commercialName || 'All Malls';
            map.set(key, [...(map.get(key) || []), location]);
            return map;
          }, new Map<string, LocationResponse[]>()),
      ),
    );
  }, [locations?.content]);

  const sideMenuSections = useMemo(() => {
    if (userType === 'location' && organization?.organizationName)
      return getLocationSideMenuSections(t, organization.organizationName, malls);
    else if (userType === 'advertiser' && organizationId) return getAdvertisersMenuSections(t);
    else if (userType === 'service')
      return getServicesMenuSections(t, hasPermissionToViewServiceSuppliers);
    else if (userType === 'backoffice') return getAdminSideMenuSections(t);
    else return [];
  }, [
    hasPermissionToViewServiceSuppliers,
    malls,
    organization?.organizationName,
    organizationId,
    t,
    userType,
  ]);

  const isActiveRoute = (menuItem: SideMenuItem): boolean => {
    const { path, otherPath, submenus } = menuItem;

    if (
      path === location.pathname ||
      otherPath === location.pathname ||
      (`/${location.pathname.split('/')?.[1]}` === path &&
        isValidUUID(location.pathname.split('/').pop()!))
    )
      return true;

    if (submenus?.length) {
      const submenu = submenus.find(({ path: submenuPath, submenus: submenuSubmenus }) => {
        return (
          location.pathname.includes(submenuPath!) ||
          submenuSubmenus?.find(({ path: submenuSubmenuPath }) =>
            location.pathname.includes(submenuSubmenuPath!),
          )
        );
      });
      if (submenu) return true;
    }

    return false;
  };

  return (
    <Container ref={ref}>
      <SideMenuLogo
        isExpanded={isExpanded}
        setIsExpanded={setIsExpanded}
        isGuest={isGuestSidebar !== false}
        disableLogoNav={userType === 'location'}
      />

      <NavigationContainer>
        {sideMenuSections.map((section: SideMenuSection, index: number) => (
          <Fragment key={index}>
            <Stack
              sx={{
                flex: section.flex ? 1 : 0,
                gap: '0.25rem',
                paddingTop: !index ? 0 : undefined,
                borderBottomWidth: index === sideMenuSections.length - 1 ? 0 : undefined,
                paddingBottom: sideMenuSections.length - 1 ? 0 : undefined,
              }}
            >
              {section.menuItems.map((menuItem) => (
                <SideMenuListItem
                  key={menuItem.label}
                  menuItem={menuItem}
                  isActiveRoute={isActiveRoute}
                  isExpanded={isExpanded}
                  showSubmenu={showSubmenu}
                  setShowSubmenu={setShowSubmenu}
                  onHoverStart={handleHoverStart}
                />
              ))}
            </Stack>

            {index < sideMenuSections.length - 1 && (
              <Divider sx={{ color: theme.palette.grey[100], margin: '0.5rem 0' }} />
            )}
          </Fragment>
        ))}
      </NavigationContainer>

      {!isGuestSidebar && <ShoppingCart isExpanded={isExpanded} />}

      {!isGuestSidebar && <GeneratedAwbs isExpanded={isExpanded} />}

      {!isGuestSidebar && <NotificationsButton isExpanded={isExpanded} userType={userType} />}

      <Divider sx={{ color: theme.palette.grey[100], margin: '0.75rem 0' }} />

      <AccountMenu isExpanded={isExpanded} />
    </Container>
  );
});

export default SideMenu;
